/**
 * EXPERIMENTAL Studio-observed evaluation-channel connection metadata reader.
 * This is not the documented maker-evaluation API or a historical run binding.
 * Observed contract: GET /powervirtualagents/bots/{schema}/channels/
 * pva-maker-evaluation/user-connections?cdsBotId={guid}&api-version=2022-03-01-preview.
 * Authentication compatibility must be verified independently for each caller.
 */
import { randomUUID } from "node:crypto";
import type { AuthProvider } from "../auth/types.js";
import { ApiError, ValidationError } from "../utils/errors.js";
import { requestWithRetry } from "../utils/http.js";
import { GUID_PATTERN } from "../utils/security.js";
import { environmentIdToApiHost } from "./connections-api.js";

export const EVALUATION_CONNECTIONS_MAX_BYTES = 1024 * 1024;
export const EVALUATION_CONNECTIONS_ERROR_MAX_BYTES = 4 * 1024;
export const EVALUATION_CONNECTIONS_TIMEOUT_MS = 15_000;
export const EVALUATION_CONNECTIONS_MAX_BINDINGS = 100;
export const EVALUATION_CONNECTIONS_MAX_OPERATIONS = 50;
export const EVALUATION_CONNECTIONS_CHANNEL = "pva-maker-evaluation";
export const EVALUATION_CONNECTIONS_API_VERSION = "2022-03-01-preview";
export const EVALUATION_BOT_SCHEMA_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,199}$/;
export const EVALUATION_ENVIRONMENT_PATTERN =
  /^(?:Default-)?[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$/;

const REFERENCE_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]{0,511}$/;
const OPERATION_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const CONNECTOR_PATTERN =
  /^(?:\/providers\/Microsoft\.PowerApps\/apis\/)?shared_[A-Za-z0-9_-]{1,100}$/;
const CONNECTION_PATTERN =
  /^(?:\/providers\/Microsoft\.PowerApps\/apis\/shared_[A-Za-z0-9_-]{1,100}\/connections\/)?[a-z0-9][a-z0-9-]{1,80}$/i;
const STATUS_LABELS = new Set(["Connected", "NotConnected", "Error", "Unauthenticated"]);
const CONTEXT = "Read Copilot Studio evaluation connections";
// Only complete, authored HTTP diagnostics from this reader receive provenance.
// A mutable Error.message or publicly constructed subclass is not trusted.
const safeHttpErrorMessages = new WeakMap<Error, string>();
export function getSafeCopilotEvaluationConnectionsErrorMessage(
  error: unknown,
): string | undefined {
  return error instanceof Error ? safeHttpErrorMessages.get(error) : undefined;
}
const DIAGNOSTIC_CODES = new Set([
  "InsufficientDelegatedPermissions",
  "Forbidden",
  "Unauthorized",
  "AuthenticationFailed",
  "AuthorizationFailed",
]);
// Closed vocabulary, not permission discovery: maker scopes are pinned by the
// official evaluation client; invoke by the execution client. All.All.ReadWrite
// was observed in the reviewed runtime denial. Unknown scope-shaped text is omitted.
const DIAGNOSTIC_SCOPE_NAMES = new Set([
  "All.All.ReadWrite",
  "CopilotStudio.MakerOperations.Read",
  "CopilotStudio.MakerOperations.ReadWrite",
  "CopilotStudio.Copilots.Invoke",
]);

export interface EvaluationConnectionsErrorDiagnostics {
  readStatus: "read" | "unavailable";
  truncated: boolean;
  codeLabels: string[];
  scopeNamesMentioned: string[];
  clientRequestId?: string;
  requestCorrelationIds?: string[];
}

function requestCorrelationIds(headers: Awaited<ReturnType<typeof requestWithRetry>>["headers"]) {
  const ids = new Set<string>();
  for (const name of [
    "request-id",
    "x-ms-request-id",
    "x-ms-correlation-request-id",
    "x-ms-correlation-id",
  ]) {
    const value = headers[name];
    if (typeof value === "string" && value.length === 36 && GUID_PATTERN.test(value))
      ids.add(value.toLowerCase());
    if (ids.size === 3) break;
  }
  return [...ids];
}

/** Only authored, sanitized diagnostics from this reader may retain their text. */
export class CopilotEvaluationConnectionsError extends ApiError {
  constructor(
    message: string,
    statusCode: number,
    public readonly diagnostics?: EvaluationConnectionsErrorDiagnostics,
  ) {
    super(
      message +
        (diagnostics === undefined
          ? ""
          : " Error diagnostics (scope names are mentions, not proven required permissions): " +
            JSON.stringify(diagnostics)),
      statusCode,
    );
  }
}

async function errorDiagnostics(
  body: Awaited<ReturnType<typeof requestWithRetry>>["body"],
): Promise<EvaluationConnectionsErrorDiagnostics> {
  const chunks: Buffer[] = [];
  let size = 0;
  let truncated = false;
  try {
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      const available = EVALUATION_CONNECTIONS_ERROR_MAX_BYTES - size;
      chunks.push(Buffer.from(chunk.subarray(0, available)));
      size += Math.min(chunk.length, available);
      if (chunk.length > available) {
        truncated = true;
        break;
      }
    }
  } catch {
    // A failed/stalled error stream does not turn an observed HTTP denial into success.
    // Its partial contents and exception are discarded, without a new request.
    return { readStatus: "unavailable", truncated, codeLabels: [], scopeNamesMentioned: [] };
  }
  const codeLabels = new Set<string>();
  const scopeNamesMentioned = new Set<string>();
  // Even a syntactically valid prefix is not the complete service error.
  if (truncated)
    return { readStatus: "unavailable", truncated: true, codeLabels: [], scopeNamesMentioned: [] };
  let data: unknown;
  try {
    data = JSON.parse(Buffer.concat(chunks).toString("utf8")) as unknown;
  } catch {
    return { readStatus: "unavailable", truncated, codeLabels: [], scopeNamesMentioned: [] };
  }
  const visit = (value: unknown, depth: number): void => {
    const item = record(value);
    if (item === null || depth > 4) return;
    for (const label of [item.code, item.error])
      if (typeof label === "string" && DIAGNOSTIC_CODES.has(label)) codeLabels.add(label);
    // Inspect only conventional error-message fields, not arbitrary response
    // properties such as credentials, URLs, claims, account names, or headers.
    for (const message of [item.message, item.error_description]) {
      if (typeof message !== "string") continue;
      // Credential-looking assignments can carry permission-shaped values.
      // Withhold the complete message instead of risking a false "scope" leak.
      // Ordinary denial prose such as "token lacks CopilotStudio.X" is not an assignment.
      if (
        /\b(?:access[_ -]?token|refresh[_ -]?token|id[_ -]?token|api[_ -]?key|client[_ -]?secret|client[_ -]?assertion|credentials?|password|passwd|secret|token|authorization|cookie|claims)\b["']?\s*[:=]/i.test(
          message,
        ) ||
        /\b(?:bearer|basic)\s+[^\s,;]+/i.test(message)
      )
        continue;
      const safe = message.replace(
        /(?:\b[A-Za-z][A-Za-z0-9+.-]*:|\/\/|www\.)[^\s<>"']+/gi,
        "[URL OMITTED]",
      );
      for (const match of safe.matchAll(
        /(?<![A-Za-z0-9_.@-])(?:CopilotStudio|Connectivity|All)\.[A-Za-z.]+(?![A-Za-z0-9_@-])/g,
      )) {
        if (scopeNamesMentioned.size === 10) break;
        const scope = match[0].replace(/\.+$/, "");
        if (DIAGNOSTIC_SCOPE_NAMES.has(scope)) scopeNamesMentioned.add(scope);
      }
    }
    for (const nested of [item.error, item.innererror, item.innerError]) visit(nested, depth + 1);
  };
  visit(data, 0);
  return {
    readStatus: "read",
    truncated,
    codeLabels: [...codeLabels],
    scopeNamesMentioned: [...scopeNamesMentioned],
  };
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function identifier(value: unknown, pattern: RegExp): string | null {
  return typeof value === "string" && pattern.test(value) ? value : null;
}

function operationIds(value: unknown) {
  if (!Array.isArray(value))
    return { values: [], count: null, omitted: null, truncated: false, incomplete: true };
  const values: string[] = [];
  for (const operation of value.slice(0, EVALUATION_CONNECTIONS_MAX_OPERATIONS)) {
    const id = identifier(record(operation)?.operationId, OPERATION_PATTERN);
    if (id !== null) values.push(id);
  }
  const omitted = value.length - values.length;
  return {
    values,
    count: value.length,
    omitted,
    truncated: value.length > EVALUATION_CONNECTIONS_MAX_OPERATIONS,
    incomplete: omitted > 0,
  };
}

function projectBindings(raw: unknown) {
  const bindings = record(raw);
  const entries = bindings === null ? null : Object.entries(bindings);
  const items = [];
  for (const [reference, value] of (entries ?? []).slice(0, EVALUATION_CONNECTIONS_MAX_BINDINGS)) {
    const referenceName = identifier(reference, REFERENCE_PATTERN);
    const binding = record(value);
    if (referenceName === null || binding === null) continue;
    const connectorId = identifier(binding.connectorId, CONNECTOR_PATTERN);
    const connectionId = identifier(binding.connectionId, CONNECTION_PATTERN);
    const status =
      typeof binding.status === "string" && STATUS_LABELS.has(binding.status)
        ? binding.status
        : "Unknown";
    const operations = operationIds(binding.operations);
    const triggerOperations = operationIds(binding.triggerOperations);
    items.push({
      referenceName,
      connectorId,
      connectionId,
      status,
      operations,
      triggerOperations,
      incomplete:
        connectorId === null ||
        connectionId === null ||
        status === "Unknown" ||
        operations.incomplete ||
        triggerOperations.incomplete,
    });
  }
  return {
    items,
    count: entries?.length ?? null,
    returned: items.length,
    omitted: entries === null ? null : entries.length - items.length,
    truncated: entries !== null && entries.length > EVALUATION_CONNECTIONS_MAX_BINDINGS,
    incomplete:
      entries === null || entries.length !== items.length || items.some((item) => item.incomplete),
  };
}

export interface EvaluationConnectionsTarget {
  environmentId: string;
  agentId: string;
  agentSchemaName: string;
}

/** O(response bytes + bindings + operations), all bounded by the transport limit. */
export function projectEvaluationConnections(raw: unknown, target: EvaluationConnectionsTarget) {
  const data = record(raw);
  if (data === null)
    throw new CopilotEvaluationConnectionsError(`${CONTEXT}: invalid metadata object`, 502);
  const flowBindings = record(data.flowBindings);
  const aiModelBindings = record(data.aiModelBindings);
  return {
    source: "experimental_runtime_user_connections",
    experimental: true,
    requestedTarget: {
      environmentId: target.environmentId,
      agentId: target.agentId,
      agentSchemaName: target.agentSchemaName,
      channel: EVALUATION_CONNECTIONS_CHANNEL,
    },
    connectorBindings: projectBindings(data.connectorBindings),
    flowBindingsCount: flowBindings === null ? null : Object.keys(flowBindings).length,
    aiModelBindingsCount: aiModelBindings === null ? null : Object.keys(aiModelBindings).length,
    verification: {
      caller: "current_authenticated_caller",
      historicalRunMapping: "not_verified",
      consent: "not_verified",
      grantedScopes: "not_verified",
      runtimeAccess: "not_verified",
      agentExecution: "not_verified",
    },
  };
}

export class CopilotEvaluationConnectionsApi {
  constructor(private readonly authProvider: AuthProvider) {}

  async getConnections(
    target: EvaluationConnectionsTarget,
    options: { includeErrorDiagnostics?: boolean } = {},
  ) {
    // Validate before token acquisition; neither host nor route is caller-configurable.
    if (
      typeof target.environmentId !== "string" ||
      !EVALUATION_ENVIRONMENT_PATTERN.test(target.environmentId) ||
      typeof target.agentId !== "string" ||
      !GUID_PATTERN.test(target.agentId) ||
      typeof target.agentSchemaName !== "string" ||
      !EVALUATION_BOT_SCHEMA_PATTERN.test(target.agentSchemaName)
    )
      throw new ValidationError(`${CONTEXT}: invalid environment, agent ID, or schema name`);
    if (
      options.includeErrorDiagnostics !== undefined &&
      typeof options.includeErrorDiagnostics !== "boolean"
    )
      throw new ValidationError(`${CONTEXT}: includeErrorDiagnostics must be boolean`);
    const host = environmentIdToApiHost(target.environmentId);
    const url =
      `https://${host}/powervirtualagents/bots/${target.agentSchemaName}` +
      `/channels/${EVALUATION_CONNECTIONS_CHANNEL}/user-connections` +
      `?cdsBotId=${target.agentId}&api-version=${EVALUATION_CONNECTIONS_API_VERSION}`;
    const identityUnavailable = () =>
      new CopilotEvaluationConnectionsError(
        `${CONTEXT}: selected-account identity unavailable or changed; no metadata returned, retry, sign-in or alternate authentication attempted.`,
        401,
      );
    const account = () => {
      try {
        return this.authProvider.getCurrentAccount() as unknown as Record<string, unknown> | null;
      } catch {
        throw identityUnavailable();
      }
    };
    const before = account();
    const guid = (value: unknown): value is string =>
      typeof value === "string" && value.length === 36 && GUID_PATTERN.test(value);
    if (
      !before ||
      typeof before.username !== "string" ||
      !before.username.trim() ||
      before.username.length > 320 ||
      before.username
        .split("")
        .some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
      !guid(before.tenantId) ||
      (before.localAccountId !== undefined && !guid(before.localAccountId))
    )
      throw identityUnavailable();
    const selected = {
      username: before.username,
      tenantId: before.tenantId,
      localAccountId: before.localAccountId,
      homeAccountId: before.homeAccountId,
      environment: before.environment,
    };
    const sameAccount = (allowHydration = false) => {
      const now = account();
      if (
        !now ||
        now.username !== selected.username ||
        now.tenantId !== selected.tenantId ||
        !guid(now.localAccountId) ||
        (selected.localAccountId !== undefined && now.localAccountId !== selected.localAccountId) ||
        (selected.localAccountId === undefined && !allowHydration) ||
        now.homeAccountId !== selected.homeAccountId ||
        now.environment !== selected.environment
      )
        throw identityUnavailable();
      selected.localAccountId = now.localAccountId;
    };
    let token: string | null | undefined;
    try {
      token = await this.authProvider.getPowerPlatformTokenSilentForCurrentAccount?.(
        selected.tenantId,
      );
    } catch {
      // Never return provider error bodies, token material, or fallback to interactive auth.
    }
    if (typeof token !== "string" || token.trim().length === 0 || /\s/.test(token))
      throw new CopilotEvaluationConnectionsError(
        `${CONTEXT}: existing silent Power Platform authentication is unavailable. No sign-in, scope, grant, identity change, or alternate API was attempted.`,
        401,
      );
    sameAccount(true);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), EVALUATION_CONNECTIONS_TIMEOUT_MS);
    const clientRequestId = randomUUID();
    let response: Awaited<ReturnType<typeof requestWithRetry>> | undefined;
    try {
      // undici.request does not follow redirects. One attempt, including 429/401/403;
      // no redirect interceptor, claims redemption, alternative audience or endpoint.
      response = await requestWithRetry(
        url,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "x-ms-client-request-id": clientRequestId,
          },
          signal: controller.signal,
          headersTimeout: EVALUATION_CONNECTIONS_TIMEOUT_MS,
          bodyTimeout: EVALUATION_CONNECTIONS_TIMEOUT_MS,
        },
        { maxAttempts: 1 },
      );
      sameAccount();
      if (response.statusCode !== 200) {
        const diagnostics =
          options.includeErrorDiagnostics === true
            ? {
                ...(await errorDiagnostics(response.body)),
                clientRequestId,
                requestCorrelationIds: requestCorrelationIds(response.headers),
              }
            : undefined;
        sameAccount();
        const error = new CopilotEvaluationConnectionsError(
          `${CONTEXT}: HTTP ${response.statusCode}; no retry or alternative authentication/API attempted. Raw response headers and error body omitted.`,
          response.statusCode,
          diagnostics,
        );
        safeHttpErrorMessages.set(error, error.message);
        throw error;
      }
      const contentType = response.headers["content-type"];
      if (
        typeof contentType !== "string" ||
        contentType.split(";", 1)[0]?.trim().toLowerCase() !== "application/json"
      )
        throw new CopilotEvaluationConnectionsError(`${CONTEXT}: expected JSON metadata`, 502);
      const length = response.headers["content-length"];
      if (
        length !== undefined &&
        (typeof length !== "string" ||
          !/^\d{1,10}$/.test(length) ||
          Number(length) > EVALUATION_CONNECTIONS_MAX_BYTES)
      )
        throw new CopilotEvaluationConnectionsError(
          `${CONTEXT}: invalid or oversized metadata length (maximum 1 MiB)`,
          413,
        );
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of response.body as AsyncIterable<Uint8Array>) {
        sameAccount();
        size += chunk.length;
        if (size > EVALUATION_CONNECTIONS_MAX_BYTES)
          throw new CopilotEvaluationConnectionsError(
            `${CONTEXT}: metadata exceeds 1 MiB safety limit`,
            413,
          );
        chunks.push(Buffer.from(chunk));
      }
      sameAccount();
      if (length !== undefined && Number(length) !== size)
        throw new CopilotEvaluationConnectionsError(
          `${CONTEXT}: incomplete metadata response`,
          502,
        );
      let raw: unknown;
      try {
        raw = JSON.parse(Buffer.concat(chunks).toString("utf8")) as unknown;
      } catch {
        throw new CopilotEvaluationConnectionsError(`${CONTEXT}: invalid JSON metadata`, 502);
      }
      return projectEvaluationConnections(raw, target);
    } catch (error) {
      if (error instanceof CopilotEvaluationConnectionsError) throw error;
      throw new CopilotEvaluationConnectionsError(
        `${CONTEXT}: transport or response stream failed or timed out; no retry attempted`,
        502,
      );
    } finally {
      try {
        response?.body.destroy();
      } catch {
        /* Cleanup must not replace an observed HTTP denial. */
      }
      controller.abort();
      clearTimeout(timer);
    }
  }
}
